import { MarketplaceMerchant } from 'APIs/marketplaces/marketplaces.types';
import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { MdOutlineSettings } from 'react-icons/md';

import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { ActionButton, MerchantDataTableContainer, MessageWrapper } from './styles';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  merchantList?: Array<MarketplaceMerchant>;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  handleMerchantManage: (merchantId: string) => void;
  limit: number;
  activePage: number;
  isErrorRequest?: boolean;
}

function MerchantDataTable({
  merchantList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  handleMerchantManage,
  activePage,
  isErrorRequest,
}: Props) {
  const ManageIcon = MdOutlineSettings as FunctionComponent;

  const columns: TableColumn<MarketplaceMerchant>[] = useMemo(
    () => [
      {
        name: 'Merchant ID',
        selector: (row: MarketplaceMerchant) => row.merchantId,
        minWidth: '150px',
      },
      {
        name: 'Business name',
        selector: (row: MarketplaceMerchant) => row.businessProfile.businessName,
        minWidth: '180px',
      },
      {
        name: 'Created at',
        selector: (row: MarketplaceMerchant) => `${DateTime.fromISO(row.createdAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        minWidth: '80px',
      },
      {
        name: 'KYB',
        selector: (row: MarketplaceMerchant) => row.verificationResult.status,
        minWidth: '80px',
      },
      {
        name: 'Payout schedule',
        selector: (row: MarketplaceMerchant) => row.payoutSchedule,
        width: '100px',
        left: true,
      },
      {
        name: 'state',
        selector: (row: MarketplaceMerchant) => row.state,
        width: '100px',
        left: true,
      },
      {
        name: 'State updated at',
        selector: (row: MarketplaceMerchant) => `${DateTime.fromISO(row.stateUpdatedAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        minWidth: '80px',
      },
      {
        name: '',
        cell: (row: MarketplaceMerchant) => (
          <div className="hover-button-wrapper">
            <ActionButton
              className="hover-button"
              title="Manage"
              onClick={(e) => {
                e.preventDefault();
                handleMerchantManage(row.merchantId);
              }}
            >
              <ManageIcon />
            </ActionButton>
          </div>
        ),
        width: '80px',
        center: true,
      },
    ],
    [ManageIcon, handleMerchantManage],
  );

  return (
    <MerchantDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={merchantList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        paginationDefaultPage={activePage}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </MerchantDataTableContainer>
  );
}

export default MerchantDataTable;
